/**
 * @file floatingActions.component.tsx
 * @path apps/website/src/components/layout/floatingActions.component.tsx
 */

import { socialLinks } from "@/config/site.config";
import { Button } from "@sirbastians/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowUpShort, Bookmark, Whatsapp } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

interface FloatingActionsProps {
  bookingLink?: string;
}

export const FloatingActions = ({ bookingLink }: FloatingActionsProps = {}) => {
  const [showActions, setShowActions] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(24); // 24px is Tailwind's bottom-6

  // Determine if the booking link is external (starts with http or //)
  const isExternalBooking =
    bookingLink?.startsWith("http") || bookingLink?.startsWith("//");

  useEffect(() => {
    const checkScroll = () => {
      // 1. Reveal buttons after 400px of scrolling, or 2px if a bookingLink exists
      setShowActions(window.scrollY > (bookingLink ? 2 : 400));

      // 2. Calculate the "docking" effect
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceToBottom = documentHeight - scrollPosition;
      
      // Tailwind bottom-6 = 24px
      // Tailwind bottom-60 = 240px (our new target resting point above the footer)
      // As distanceToBottom decreases below 216px (240 - 24), we push the button up.
      const calculatedBottom = Math.max(24, 240 - distanceToBottom);
      
      setBottomOffset(calculatedBottom);
    };

    window.addEventListener("scroll", checkScroll, { passive: true });
    // Initial check
    checkScroll();
    
    return () => window.removeEventListener("scroll", checkScroll);
  }, [bookingLink]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const whatsappLink = socialLinks.find(
    (link) => link.label === "WhatsApp",
  )?.href;

  return (
    <div 
      className="fixed right-6 md:right-14 z-[60] flex flex-col items-end gap-3"
      style={{ bottom: `${bottomOffset}px` }} // Inline style for smooth 1:1 scroll tracking
    >
      <AnimatePresence>
        {showActions && (
          <div className="flex flex-col items-end gap-3">
            {bookingLink && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.5, y: 20 }}
                transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
              >
                <Button
                  variant="glass"
                  size="icon"
                  asChild
                  className="h-12 w-12 rounded-full shadow-lg"
                >
                  {isExternalBooking ? (
                    <a
                      href={bookingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Book external service"
                    >
                      <Bookmark className="h-5 w-5" />
                    </a>
                  ) : (
                    <Link to={bookingLink} aria-label="Book internal service">
                      <Bookmark className="h-5 w-5" />
                    </Link>
                  )}
                </Button>
              </motion.div>
            )}

            {/* WHATSAPP BUTTON */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: 20 }}
              transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
            >
              <Button
                variant="accent"
                size="icon"
                asChild
                className="h-12 w-12 rounded-full shadow-lg"
              >
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Contact us on WhatsApp"
                >
                  <Whatsapp className="h-5 w-5 fill-current" />
                </a>
              </Button>
            </motion.div>

            {/* SCROLL TO TOP BUTTON */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <Button
                variant="glass"
                size="icon"
                onClick={scrollToTop}
                aria-label="Scroll to top"
                className="h-12 w-12 rounded-full shadow-lg"
              >
                <ArrowUpShort className="h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};